import React, { useState } from 'react';
import { useToast } from './ToastContainer';
import {
  useBookings,
  useCancelBooking,
  useMarkCompleted,
  useMarkNoShow,
  useApproveBooking,
  useRejectBooking,
  type Booking
} from '../hooks/useBookings';

interface BookingsTabProps {
  agentHandle: string;
}

const BookingsTab: React.FC<BookingsTabProps> = ({ agentHandle }) => {
  const { showError, showSuccess } = useToast();
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [appointmentToCancel, setAppointmentToCancel] = useState<Booking | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelSource, setCancelSource] = useState<'customer' | 'manual' | 'system'>('manual');

  // React Query hooks
  const { data, isLoading: loading, error } = useBookings(agentHandle, filterStatus);
  const cancelMutation = useCancelBooking();
  const completedMutation = useMarkCompleted();
  const noShowMutation = useMarkNoShow();
  const approveMutation = useApproveBooking();
  const rejectMutation = useRejectBooking();

  const bookings = data?.bookings || [];

  // Show error if query failed
  React.useEffect(() => {
    if (error) {
      showError('Failed to load bookings');
    }
  }, [error, showError]);

  // Filter bookings by search term
  const filteredBookings = bookings.filter(booking => {
    const searchLower = searchTerm.toLowerCase();
    return (
      booking.customerName.toLowerCase().includes(searchLower) ||
      booking.customerEmail.toLowerCase().includes(searchLower) ||
      booking.service.toLowerCase().includes(searchLower)
    );
  });

  // Group bookings by time (upcoming vs past)
  const now = new Date();
  const upcomingBookings = filteredBookings.filter(b => new Date(b.appointmentTime) >= now);
  const pastBookings = filteredBookings.filter(b => new Date(b.appointmentTime) < now);

  const handleCancelClick = (booking: Booking) => {
    setAppointmentToCancel(booking);
    setShowCancelModal(true);
    setCancelReason('');
    setCancelSource('manual');
  };

  const handleSubmitCancellation = async () => {
    if (!appointmentToCancel) return;

    cancelMutation.mutate(
      {
        agentHandle,
        bookingId: appointmentToCancel.id,
        reason: cancelReason,
        source: cancelSource
      },
      {
        onSuccess: () => {
          showSuccess('Appointment cancelled successfully!');
          setShowCancelModal(false);
          setAppointmentToCancel(null);
          setCancelReason('');
        },
        onError: (error) => {
          showError((error as Error).message || 'Failed to cancel appointment');
        }
      }
    );
  };

  const handleMarkCompleted = (booking: Booking) => {
    completedMutation.mutate(
      {
        agentHandle,
        bookingId: booking.id
      },
      {
        onSuccess: () => {
          showSuccess('Appointment marked as completed!');
        },
        onError: (error) => {
          showError((error as Error).message || 'Failed to mark as completed');
        }
      }
    );
  };

  const handleMarkNoShow = (booking: Booking) => {
    noShowMutation.mutate(
      {
        agentHandle,
        bookingId: booking.id
      },
      {
        onSuccess: () => {
          showSuccess('Appointment marked as no-show!');
        },
        onError: (error) => {
          showError((error as Error).message || 'Failed to mark as no-show');
        }
      }
    );
  };

  const getStatusBadge = (status: string) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
      confirmed: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
      cancelled: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
      completed: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
      'no-show': 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300',
    };

    return (
      <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[status as keyof typeof styles] || 'bg-gray-100 text-gray-800'}`}>
        {status}
      </span>
    );
  };

  const BookingCard = ({ booking, isUpcoming }: { booking: Booking; isUpcoming: boolean }) => (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 hover:shadow-md transition-shadow">
      <div className="flex justify-between items-start mb-3">
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            {booking.customerName}
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">{booking.customerEmail}</p>
          {booking.customerPhone && (
            <p className="text-sm text-gray-600 dark:text-gray-400">{booking.customerPhone}</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {getStatusBadge(booking.status)}
        </div>
      </div>

      <div className="space-y-2 mb-3">
        <div className="flex items-center text-sm text-gray-700 dark:text-gray-300">
          <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
          {booking.service}
        </div>

        <div className="flex items-center text-sm text-gray-700 dark:text-gray-300">
          <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          {new Date(booking.appointmentTime).toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          })}
        </div>

        <div className="flex items-center text-sm text-gray-700 dark:text-gray-300">
          <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {new Date(booking.appointmentTime).toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit'
          })}
        </div>
      </div>

      {/* Pending appointments: Show Approve and Reject buttons */}
      {booking.status === 'pending' && (
        <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
          <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-3 mb-3">
            <p className="text-xs text-yellow-800 dark:text-yellow-300">
              This booking is pending your approval
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={async () => {
                try {
                  await approveMutation.mutateAsync({
                    agentHandle,
                    bookingId: booking.id
                  });
                  showSuccess('Booking approved and added to calendar');
                } catch (err) {
                  showError(err instanceof Error ? err.message : 'Failed to approve booking');
                }
              }}
              disabled={approveMutation.isPending}
              className="flex-1 px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {approveMutation.isPending ? 'Approving...' : '✓ Approve'}
            </button>
            <button
              onClick={async () => {
                const reason = window.prompt('Reason for rejection (optional):');
                if (reason === null) return; // User cancelled

                try {
                  await rejectMutation.mutateAsync({
                    agentHandle,
                    bookingId: booking.id,
                    reason: reason || undefined
                  });
                  showSuccess('Booking rejected and customer notified');
                } catch (err) {
                  showError(err instanceof Error ? err.message : 'Failed to reject booking');
                }
              }}
              disabled={rejectMutation.isPending}
              className="flex-1 px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {rejectMutation.isPending ? 'Rejecting...' : '✗ Reject'}
            </button>
            <button
              disabled
              className="px-4 py-2 text-sm font-medium text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-700 rounded-md cursor-not-allowed"
              title="Reschedule coming soon"
            >
              📅
            </button>
          </div>
        </div>
      )}

      {/* Upcoming confirmed appointments: Show Cancel button */}
      {booking.status === 'confirmed' && isUpcoming && (
        <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={() => handleCancelClick(booking)}
            className="w-full px-4 py-2 text-sm font-medium text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors"
          >
            Cancel Appointment
          </button>
        </div>
      )}

      {/* Past confirmed appointments: Show Completed and No-Show buttons */}
      {booking.status === 'confirmed' && !isUpcoming && (
        <div className="pt-3 border-t border-gray-200 dark:border-gray-700 flex gap-2">
          <button
            onClick={() => handleMarkCompleted(booking)}
            className="flex-1 px-4 py-2 text-sm font-medium text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-md transition-colors"
          >
            Mark Completed
          </button>
          <button
            onClick={() => handleMarkNoShow(booking)}
            className="flex-1 px-4 py-2 text-sm font-medium text-yellow-600 hover:text-yellow-700 dark:text-yellow-400 dark:hover:text-yellow-300 hover:bg-yellow-50 dark:hover:bg-yellow-900/20 rounded-md transition-colors"
          >
            Mark No-Show
          </button>
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with filters */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Appointments</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Manage all your bookings in one place
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          {/* Search */}
          <div className="relative">
            <input
              type="text"
              placeholder="Search customers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            <svg className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>

          {/* Status Filter */}
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="all">All Status</option>
            <option value="pending">Pending Approval</option>
            <option value="confirmed">Confirmed</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
            <option value="no-show">No Show</option>
          </select>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">Total</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">{bookings.length}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">Upcoming</p>
          <p className="text-2xl font-bold text-green-600 dark:text-green-400">{upcomingBookings.length}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">Past</p>
          <p className="text-2xl font-bold text-gray-600 dark:text-gray-400">{pastBookings.length}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">Cancelled</p>
          <p className="text-2xl font-bold text-red-600 dark:text-red-400">
            {bookings.filter(b => b.status === 'cancelled').length}
          </p>
        </div>
      </div>

      {/* Bookings List */}
      {filteredBookings.length === 0 ? (
        <div className="text-center py-12">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No appointments</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {searchTerm ? 'No appointments match your search.' : 'No appointments found.'}
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Upcoming Appointments */}
          {upcomingBookings.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Upcoming Appointments ({upcomingBookings.length})
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {upcomingBookings.map((booking) => (
                  <BookingCard key={booking._id} booking={booking} isUpcoming={true} />
                ))}
              </div>
            </div>
          )}

          {/* Past Appointments */}
          {pastBookings.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Past Appointments ({pastBookings.length})
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {pastBookings.map((booking) => (
                  <BookingCard key={booking._id} booking={booking} isUpcoming={false} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Cancel Modal */}
      {showCancelModal && appointmentToCancel && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl max-w-md w-full p-6">
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                Cancel Appointment
              </h3>
              <button
                onClick={() => setShowCancelModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <p className="font-medium text-gray-900 dark:text-white">{appointmentToCancel.customerName}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">{appointmentToCancel.service}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {new Date(appointmentToCancel.appointmentTime).toLocaleString()}
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Cancellation Source
                </label>
                <select
                  value={cancelSource}
                  onChange={(e) => setCancelSource(e.target.value as 'customer' | 'manual' | 'system')}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="customer">Customer cancelled</option>
                  <option value="manual">Manual/External cancellation</option>
                  <option value="system">System cancellation</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Reason (optional)
                </label>
                <textarea
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  placeholder="Enter cancellation reason..."
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                  rows={3}
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowCancelModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitCancellation}
                className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
              >
                Cancel Appointment
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingsTab;
